import React, { useContext, useEffect, useState } from "react";

import ShIcon from "../../shared/components/sh-icon/sh-icon";
import ShLink from "../../shared/components/sh-link/sh-link";
import { Media } from "../../shared/contexts/media/MediaContext";
import { UiStateContext } from "../../shared/contexts/ui-state/UiStateContext";
import useFeatureToggle from "../../shared/hooks/use-feature-toggle/useFeatureToggle";
import { FeatureToggle, GlobalNavContent, IconPack } from "../../shared/models/app-data-model";
import MegaMenu from "../mega-menu/mega-menu";
import "../shared/navigation-shared.scss";
import "./condensed-nav-desktop.scss";

export type CondensedNavDesktopProps = {
  content: GlobalNavContent;
  featureToggles: FeatureToggle[];
  iconPack?: IconPack[];
};

const CondensedNavDesktop: React.FC<CondensedNavDesktopProps> = ({ content, featureToggles, iconPack }: CondensedNavDesktopProps) => {
  const uiStateContext = useContext(UiStateContext);
  const megaMenuEnabled = useFeatureToggle("Corewell-health-mega-menu", featureToggles);
  const [selectedNavIndex, setSelectedNavIndex] = useState<number>();

  const handleMegaMenuBtnClick = (index: number) => {
    setSelectedNavIndex(index);

    if (selectedNavIndex === index || !uiStateContext.megaMenuOpen) {
      uiStateContext?.setMegaMenuOpen?.(!uiStateContext.megaMenuOpen);
    }
  };

  return (
    <Media greaterThanOrEqual="desktop">
      <nav id="global-nav-desktop" className="condensed-nav-desktop">
        {content?.primary_nav?.map((item, index) => {
          // This is required on CS, so this acts as the "default link"
          const navItemWithLink = item.primary_nav_item?.reference.find((el) => el.url || el.link);
          // This will only get rendered if the toggle is on, and if the mega menu content exists
          const megaMenuLink = item.primary_nav_item?.mega_menu?.find(
            (el) => el.column_1_content || el.column_2_content || el.column_3_content || el.column_4_content
          );

          if ((navItemWithLink && !megaMenuLink) || !megaMenuEnabled) {
            return (
              <ShLink
                key={index}
                featureToggles={featureToggles}
                title={item.primary_nav_item.title}
                className={`global-nav-item non-mega-menu-link`}
                reference={item.primary_nav_item.reference}
              />
            );
          } else if (megaMenuLink && megaMenuEnabled) {
            return (
              <div className="mm-global-nav-item-container">
                <button
                  className="global-nav-item mega-menu-global-nav-item unstyled-btn"
                  data-testid="mega-menu-btn"
                  onClick={() => handleMegaMenuBtnClick(index)}
                  key={`mega-menu-btn-${index}`}
                  // The "mm-nav-btn" at the end of this id cannot be changed, because this is what prevents a mega menu from closing,
                  // should a user click on another global nav item that has a mega menu entry (i.e. this button element)
                  // The end of this id is used within the 'useComponentVisible' hook within the <MegaMenu />
                  id={`${index}-mm-nav-btn`}
                >
                  {item.primary_nav_item.title}
                  <ShIcon
                    iconPack={iconPack}
                    iconName="caret_down"
                    className="mm-nav-caret-icon"
                    elemId={`primary-nav-item-${index}-icon`}
                  />
                </button>
                <div
                  className={`mm-cyan-border-bottom ${selectedNavIndex === index && uiStateContext.megaMenuOpen && "mm-link-active"}`}
                ></div>
              </div>
            );
          }
        })}
      </nav>
      {uiStateContext?.megaMenuOpen && selectedNavIndex !== undefined && megaMenuEnabled && (
        <MegaMenu
          content={content?.primary_nav[selectedNavIndex]?.primary_nav_item?.mega_menu}
          iconPack={iconPack}
          featureToggles={featureToggles}
        />
      )}
    </Media>
  );
};

export default CondensedNavDesktop;
